package com.pincer.app

import com.pincer.app.viewmodel.app
import com.pincer.app.viewmodel.Node
import com.pincer.client.Changes
import com.pincer.client.DEFAULT_SERVER_URL
import com.pincer.client.DEFAULT_WEBSOCKET_URL
import com.pincer.client.JobCentre
import com.pincer.client.OnChange
import com.pincer.client.Client
import com.pincer.core.contracts.PincerRequest
import com.pincer.core.contracts.TEMPLATE_PID
import com.pincer.core.CycleSecret
import com.pincer.core.functions.getAnswer
import com.pincer.core.functions.getRolesForParticipant
import com.pincer.core.functions.newChatId
import com.pincer.core.functions.newQuestionId
import com.pincer.core.functions.readable
import com.pincer.core.functions.readablePid
import com.pincer.core.functions.termsNeeded
import com.pincer.core.functions.toPersonalPid
import com.pincer.core.functions.upsertAnswer
import com.pincer.core.functions.validateAnswer
import com.pincer.core.functions.validateCode
import com.pincer.core.functions.validateCredential
import com.pincer.core.functions.validateEmail
import com.pincer.core.functions.validateId
import com.pincer.core.functions.validateLocale
import com.pincer.core.functions.validateNameOfPerson
import com.pincer.core.functions.validateOptions
import com.pincer.core.functions.validatePid
import com.pincer.core.functions.validatePrincipalName
import com.pincer.core.functions.validateQuestion
import com.pincer.core.functions.validateQuestionCode
import com.pincer.core.functions.validateRegex
import com.pincer.core.functions.validateRoles
import com.pincer.core.functions.validateRole
import com.pincer.core.functions.validateRules
import com.pincer.core.functions.validateServerUrl
import com.pincer.core.functions.validateWebSocketUrl
import com.pincer.core.functions.validateText
import com.pincer.core.functions.validateZone
import com.pincer.core.InvalidData
import com.pincer.core.LockedPrincipal
import com.pincer.core.Logger
import com.pincer.core.model.Device
import com.pincer.core.model.drafts.AnswerDraft
import com.pincer.core.model.drafts.PincerDraft
import com.pincer.core.model.drafts.ParticipantDraft
import com.pincer.core.model.drafts.QuestionDraft
import com.pincer.core.model.tree.PincerType
import com.pincer.core.model.tree.Answer
import com.pincer.core.model.tree.CANDIDATES_SCOPE
import com.pincer.core.model.tree.SELECTED_SCOPE
import com.pincer.core.model.tree.Chat
import com.pincer.core.model.tree.CHATS_SCOPE
import com.pincer.core.model.tree.DESCRIPTION_SCOPE
import com.pincer.core.model.tree.NAME_SCOPE
import com.pincer.core.model.tree.Participant
import com.pincer.core.model.tree.PARTICIPANTS_ANSWERS_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_ROLES_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_RULES_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_SCOPE
import com.pincer.core.model.tree.PERMISSIONS_SCOPE
import com.pincer.core.model.tree.Question
import com.pincer.core.model.tree.QuestionFormat
import com.pincer.core.model.tree.QUESTIONS_SCOPE
import com.pincer.core.model.tree.RULES_SCOPE
import com.pincer.core.PincerException
import com.pincer.core.YouNeedToLogIn
import kotlin.js.JsName
import kotlin.concurrent.Volatile
import kotlinx.coroutines.Job
import com.russhwolf.settings.Settings
import com.pincer.app.viewmodel.Action
import com.pincer.app.viewmodel.TreeDiff
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.time.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant

class Application(private val coroutineScope: CoroutineScope, private val log: Logger, settings: Settings = Settings()) {

    private val changes = Changes(log)

    @JsName("listenToChanges")
    fun listenToChanges(onChange: OnChange) = changes.listen(onChange)

    private val state = ApplicationState(log, settings, { changes.emit() })

    private val jobCentre = JobCentre(coroutineScope = coroutineScope, log = log)

    private var loadedLocale = "Not yet loaded"
    private fun loadLocale(locale: String) {
        if (locale == loadedLocale) return
        log.i("Loading locale $locale over $loadedLocale")
        I18n.load(locale, log)
        loadedLocale = locale
        if (state.stored.fallbackLocale != locale) {
            log.i("Also updating fallback locale")
            state.stored.fallbackLocale = locale
            // TODO neat to do the locale here, but what about the zone?
        }
    }

    fun unexpectedError() = state.transient.unexpectedError

    @JsName("location")
    fun location() = state.transient.location

    // The tree the person was last shown. Actions can only be performed through it (see perform).
    @Volatile private var shown: Node? = null

    @JsName("buildViewModel")
    fun buildViewModel(): Node {
        loadLocale(state.locale())
        val tree = app(state)
        shown = tree
        return tree
    }

    /*
    Rebuilds the tree and says what changed since the person was last shown it, so a renderer can redraw just that
    (keeping focus, half-typed text and a screen reader's place) and an agent can be told just that. The first call
    reports everything as added.
    */
    @JsName("update")
    fun update(): TreeDiff {
        val before = shown
        return TreeDiff.between(before, buildViewModel())
    }

    /*
    The one way in for renderers, the REPL and agents: do what the control at this compound key does, with this
    value (what was typed, the choice picked, "true" or "false" for a checkbox, nothing for a button). The control
    has to be in the tree the person was last shown, so nobody can do what isn't on screen. Throws InvalidData
    if there's no such control or the value doesn't fit it. Returns the job doing the work (see performAndWait).
    */
    @JsName("perform")
    fun perform(compoundKey: String, value: String?): Job {
        val tree = shown ?: buildViewModel()
        val node = tree.find(compoundKey) ?: throw InvalidData("There's no $compoundKey on screen")
        val problem = node.problemWithValue(value)
        if (problem != null) throw InvalidData(problem)
        log.i("Performing ${node.action} at ${node.compoundKey()}")
        /*
        What someone is working in stays open. A node can be open at first because of what's in it (a field with
        nothing saved, or a problem); without this, saving a good value would close it under the person's hands.
        */
        var around = node.parent
        while (around != null) {
            if (around.open == true) state.transient.opened.getOrPut(around.compoundKey()) { true }
            around = around.parent
        }
        val text = value ?: ""
        val flag = value == "true"
        return when (val action = node.action!!) {
            Action.PrincipalTriggerCode         -> principalTriggerCode(text)
            Action.PrincipalConfirmCode         -> principalConfirmCode(text)
            Action.SetPersonalName              -> setPersonalName(text)
            Action.LogoutThisDevice             -> logoutThisDevice()
            Action.LogoutAllDevices             -> logoutAllDevices()
            Action.SetLocale                    -> setLocale(text)
            Action.SetZone                      -> setZone(text)
            Action.SetShowAdvancedOptions       -> setShowAdvancedOptions(flag)
            Action.SetServerUrl                 -> setServerUrl(text)
            Action.SetWebSocketUrl              -> setWebSocketUrl(text)
            Action.ResetDevice                  -> resetDevice()
            is Action.SetOpen                   -> setOpen(action.compoundKey, flag)
            is Action.LoadPincer                -> loadPincer(action.pid)
            Action.SetNewPincerName             -> setNewPincerName(text)
            Action.CreateNewPincer              -> createNewPincer()
            is Action.ConfirmCandidate          -> confirmCandidate(action.candidateId)
            Action.SetDraftChat                 -> setDraftChat(text)
            Action.AddChat                      -> addChat()
            is Action.SetAnswer                 -> setAnswer(action.questionId, text)
            Action.SetPincerName                -> setPincerName(text)
            Action.SetPincerDescription         -> setPincerDescription(text)
            Action.SetUseOpenRole               -> setUseOpenRole(flag)
            Action.SetOpenRole                  -> setOpenRole(text)
            Action.SetPincerRules               -> setPincerRules(text)
            Action.MakeTemplate                 -> makeTemplate()
            Action.SetRedRules                  -> setRedRules(text)
            Action.SetGreenRules                -> setGreenRules(text)
            Action.SetStencil                   -> setStencil(flag)
            is Action.SetQuestionCode           -> setQuestionCode(action.questionId, text)
            is Action.SetQuestionWording        -> setQuestionWording(action.questionId, text)
            is Action.SetQuestionFormat         -> setQuestionFormat(action.questionId, text)
            is Action.SetQuestionHelpText       -> setQuestionHelpText(action.questionId, text)
            is Action.SetQuestionOptions        -> setQuestionOptions(action.questionId, text)
            is Action.SetQuestionRegex          -> setQuestionRegex(action.questionId, text)
            is Action.SetQuestionRoles          -> setQuestionRoles(action.questionId, text)
            Action.SetNewQuestionCode           -> setNewQuestionCode(text)
            Action.SetNewQuestionWording        -> setNewQuestionWording(text)
            Action.SetNewQuestionFormat         -> setNewQuestionFormat(text)
            Action.SetNewQuestionHelpText       -> setNewQuestionHelpText(text)
            Action.SetNewQuestionOptions        -> setNewQuestionOptions(text)
            Action.SetNewQuestionRegex          -> setNewQuestionRegex(text)
            Action.SetNewQuestionRoles          -> setNewQuestionRoles(text)
            Action.AddQuestion                  -> addQuestion()
        }
    }

    // For callers that can wait (the REPL, the MCP server, tests): perform, then wait for the work to finish
    suspend fun performAndWait(compoundKey: String, value: String?) = perform(compoundKey, value).join()

    @JsName("start")
    fun start(initialLocation: String, fresh: Boolean) {
        log.i("============== Pincer App Starting ==============")
        if (fresh) {
            log.i("Running from fresh, resetting stored state")
            state.resetStoredState()
        }
        // state.stored.logAll()
        state.transient.location = initialLocation
        wrap("InitialClientSetup") {
            state.resetRemoteState(coroutineScope)
            if (initialLocation.isEmpty()) {
                log.i("No initial location specified")
            }
            else if (validatePid(initialLocation) == null) {
                log.i("Initial location is pid $initialLocation - loading")
                if (state.remote!!.loggedOut()) {
                    log.i("Initial location while not logged in, setting up anonymously")
                    state.remote!!.principalAnonymous(locale = state.stored.fallbackLocale, zone = state.stored.fallbackZone)
                    state.transient.arrivedAnonymous = true
                }
                try {
                    state.remote!!.load(initialLocation)
                    state.transient.pid = initialLocation
                    state.assertPincerIsReady()
                    if (!state.isParticipant()) {
                        if (state.pincer()!!.pincerType == PincerType.TEMPLATE && !(state.pincer()!!.forkRole.isNullOrEmpty())) {
                            log.i("Forking template from initial location")
                            state.transient.pid = state.remote!!.fork(state.transient.pid!!)
                            state.transient.location = state.transient.pid
                        }
                        else if (!state.pincer()!!.openRole.isNullOrEmpty()) {
                            // TODO settable limit for number of participants coming in this way?
                            // (will need a specific exception I think and an configurable message)
                            log.i("Joining via open role ${state.pincer()!!.openRole}")
                            addParticipant(state.remote!!.principalId!!, state.pincer()!!.openRole!!)
                        }
                        else {
                            log.w("Not a participant, but neither template or open")
                        }
                    }
                }
                catch (pe: PincerException) {
                    log.w("Ignoring problem loading pincer specified in location $initialLocation", pe)
                    state.transient.pid = null
                    state.transient.location = null
                }
                catch (unexpectedError: Exception) {
                    log.w("Some other problem loading from location", unexpectedError)
                    state.transient.unexpectedError = unexpectedError
                    state.transient.pid = null
                    state.transient.location = null
                }
            }
            else {
                log.w("Taking no action on initial location: $initialLocation")
            }
            log.i("App started")
        }
    }

    fun stop() = wrap("stop") {
        log.i("Stopping application")
        state.remote?.stop()
        log.i("============== Pincer App Stopped ==============")

    }

    /*
    TODO Locale and Zone detection
    There are two ways to create the principal on the server (anonymous and confirm code).
    Both allow for the locale and zone to be specified.
    If these are left as null, the server will drop in the default values.
    There's some overlapping things for getting detected values in place:
    - Not every call to confirm code will pay attention to the specified locale/zone ... pre-existing principals keep theirs.
    - Contract layers that convert http into some other format (i.e. request or rest) could use the language header to fill locale.
    - There's no equivalent thing for zone from headers unfortunately.
    - The normal browser headers won't be available if we are doing everything via the websocket.
    - Timing of the fallback to default matters here in that it could look like the user has expressed a preference when they haven't.
    - When the Application builds the remoteState/client it has access to the stored state and can pass through the fallbacks.
    - One neat way to use the browser headers would be to echo the header in a transient field on the Device when registering.
    - Although this would make it look like a device could have a locale different to the principal.
    - Even if that made sense and we added that as a permanent feild on the device, zone couldn't vary in the same way.
    - Difference in logic for pure-client vs. full-application flow: force client calls to specify at least null, but handle it all in the app.
    - If so, perhaps all we need is an early bit of code in application to initialize stored state fallbacks well.
    - Timedate libraries / multiplatform could sort the zone locally, perhaps a forced http interaction to get the locale echo.
    - At the moment, the stored state constuctor means we don't distinguish between first set up and read-from-settings runs.
    - Simplest way to do this might be to make fallbacks nullable as a flag. Or use a dedicated setting e.g. initializedAt.

    Fallback locale/zone suddenly looked like a bit of a leak ... if we keep updating it to what we get from the server,
    logout might leave a trace of the pevious account. Maybe we should call them detectedZone and detectedLocale instead
    and have them stick on their oringal values when principals come and go. Or only update them when settings change logged out.
    */

    /*
    Common handling for processing controller style actions.
    Plus, we run all the actions in the jobCentre, which means appication actions can avoid suspend blocking.
    Unlike the version of this in the client, blocks don't return, so we can terminate
    exceptions here by recorind the problem in the state.
    Originally we also did a mutex.withLock in here, but it felt a bit heavy handed.
    Aonther idea here was to emit changes outside the launch, which means the view model could show what's happening.
    */
    private fun wrap(id: String, f:suspend () -> Unit) = jobCentre.launch(id) {
        log.i("Wrapping $id")
        try {
            f()
        }
        catch (yntli: YouNeedToLogIn) {
            log.w("Swallowing the YouNeedToLogin")
            // TODO ...
            // This is sufficient when client has logged us out since problem will be clear to the user from the login screen appearing.
            // But are there cases where we would not have been logged in before? If so, do we need something like a toaster?
        }
        catch (throwable: Throwable) {
            log.e("Unexpected throwable: ${throwable.message}")
            log.i(throwable.stackTraceToString())
            state.transient.unexpectedError = throwable
        }
        changes.emit()
    }

    private fun setPersonalName(personalName: String) = wrap("setPersonalName") {
        state.assertPersonalIsReady()
        state.personalUiSelf()!!.name = personalName
        if (validateNameOfPerson(personalName) != null) {
            log.i("Ignoring call to setPersonalName as invalid : $personalName")
        }
        else if (personalName == state.personalSelf()!!.name) {
            log.i("Ignoring call to setPersonalName as is current value : $personalName")
            state.personalUiSelf()!!.name = null
        }
        else {
            log.i("Setting personalName to $personalName")
            val previousName = state.personalSelf()!!.name
            state.personalUiSelf()!!.name = null
            state.personalSelf()!!.name = personalName
            state.remote!!.syncUpPersonal()
            // Someone who joined this pincer before giving a name (by its link, with no account) gets it here too.
            // Their other pincers keep the old name for now (see TODO.md).
            val self = state.pincerSelf()
            if (self != null && (self.name == null || self.name == previousName)) {
                self.name = personalName
                state.remote!!.syncUp(state.transient.pid!!)
            }
        }
    }

    private fun setLocale(locale: String) = wrap("setLocale") {
        if (validateLocale(locale) != null) {
            log.i("Ignoring call to setLocale as invalid : $locale")
        }
        else if (locale == state.locale()) {
            log.i("Ignoring call to setLocale as is current value : $locale")
        }
        else {
            log.i("Setting fallback locale to $locale")
            state.stored.fallbackLocale = locale
            if (state.personalIsReady()) {
                state.personalSelf()!!.locale = locale
                state.remote!!.syncUpPersonal()
            }
            else {
                log.w("Not able to set locale on server as not logged in")
            }
        }
    }

    private fun setZone(zone: String) = wrap("setZone") {
        if (validateZone(zone) != null) {
            log.w("Ignoring call to setZone as invalid : $zone")
        }
        else {
            log.i("Setting fallback zone to $zone")
            state.stored.fallbackZone = zone
            if (state.personalIsReady()) {
                state.personalSelf()!!.zone = zone
                state.remote!!.syncUpPersonal()
            }
            else {
                log.w("Not able to set zone on server as not logged in")
            }
        }
    }

    private fun setServerUrl(serverUrl: String) = wrap("setServerUrl", {
        if (validateServerUrl(serverUrl) != null) {
            log.w("Ignoring setServerUrl as invalid: $serverUrl")
        }
        else {
            log.i("Setting server url to $serverUrl")
            state.stored.serverUrl = serverUrl
        }
    })

    private fun setWebSocketUrl(webSocketUrl: String) = wrap("setWebSocketUrl", {
        if (validateWebSocketUrl(webSocketUrl) != null) {
            log.w("Ignoring setWebSocketUrl as invalid: $webSocketUrl")
        }
        else {
            log.i("Setting websocket url to $webSocketUrl")
            state.stored.webSocketUrl = webSocketUrl
        }
    })

    private fun resetDevice() = wrap("resetDevice", {
        log.i("Resetting device")
        state.resetStoredState()
        state.resetTransientState()
        state.resetRemoteState(coroutineScope)
        changes.emit()
    })

    private fun setShowAdvancedOptions(showAdvancedOptions: Boolean) = wrap("setShowAdvancedOptions") {
        state.stored.showAdvancedOptions = showAdvancedOptions.toString()
    }

    private fun setOpen(compoundKey: String, open: Boolean) = wrap("setOpen") {
        state.transient.opened[compoundKey] = open
    }

    private fun setNewPincerName(name: String) = wrap("setNewPincerName", {
        state.transient.newPincerName = name
    })

    private suspend fun loadPincerInner(pid: String) {
        if (validatePid(pid) != null) {
            log.w("Not loading pid as invalid : $pid")
            return
        }
        if (state.pincerIsReady()) {
            if (state.transient.pid == pid) {
                log.w("Pincer already loaded : $pid")
                return
            }
            log.i("Unloading ${state.transient.pid}")
            state.remote!!.unload(state.transient.pid!!)
        }
        log.i("Loading pincer $pid")
        state.transient.location = pid
        state.transient.pid = pid
        state.remote!!.load(pid)
    }

    private fun createNewPincer() = wrap("createNewPincer", {
        log.i("Create new pincer : ${state.transient.newPincerName}")
        state.assertPersonalIsReady()
        if (state.transient.newPincerName == null) state.transient.newPincerName = ""
        if (validateText(text = state.transient.newPincerName, minLength = 3) != null) {
            log.w("Ignoring call to creatNewPincer as name is invalid : ${validateText(state.transient.newPincerName)}")
        }
        else {
            log.i("Requesting fork of $TEMPLATE_PID")
            val pid = state.remote!!.fork(pid = TEMPLATE_PID)
            log.i("New pincer created : $pid")
            state.transient.location = pid
            state.transient.pid = pid
            log.i("Writing name (and name/locale/zone) to newly forked pincer")
            state.pincer()!!.name = state.transient.newPincerName
            state.transient.newPincerName = null
            if (state.personalSelf()?.name != null) state.pincerSelf()!!.name = state.personalSelf()?.name
            if (state.personalSelf()?.locale != null) state.pincerSelf()!!.locale = state.personalSelf()?.locale!!
            if (state.personalSelf()?.zone != null) state.pincerSelf()!!.zone = state.personalSelf()?.zone!!
            log.i("Running sync to submit changes")
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    @JsName("loadPincer")
    fun loadPincer(pid: String) = wrap("loadPincer", {
        loadPincerInner(pid)
    })

    private fun confirmCandidate(id: String) = wrap("confirmCandidate", {
        state.assertParticipant()
        val candidate = state.pincer()!!.candidates[id]!!
        state.pincer()!!.selected = candidate.duplicate()
        log.i("Running sync to confirm candidate ${candidate.id()}")
        state.remote!!.syncUp(state.transient.pid!!)
    })

    private fun setDraftChat(wording: String) = wrap("setDraftChat", {
        state.assertParticipant()
        if (wording.trim() == "") {
            // unlike some other fields, we don't mind an empty box
            state.transient.draftChat = null
        } else {
            state.transient.draftChat = wording
        }
    })

    private fun addChat() = wrap("addChat", {
        state.assertParticipant()
        if (state.transient.draftChat == null || state.transient.draftChat!!.trim() == "") {
            state.transient.draftChat = null
            log.w("Ignoring call to addChat as wording is blank")
        }
        else if (validateText(state.transient.draftChat) != null) {
            log.w("Ignoring call to addChat as wording is invalid : ${validateText(state.transient.draftChat)}")
        }
        else {
            state.pincer()!!.chats[newChatId(state.pincer()!!)] = Chat(principalId = state.remote!!.principalId!!, wording = state.transient.draftChat!!.trim())
            state.transient.draftChat = null
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun addParticipant(principalId: String, roles: String) = wrap("addParticipant", {
        state.assertPincerIsReady()
        if (validateRoles(roles) != null) {
            log.w("Ignoring call to addParticipant as roles invalid : $roles")
        }
        if (validateId(principalId) != null) {
            log.w("Ignoring call to addParticipant as principalId invalid : $principalId")
        }
        else {
            if (state.isParticipant()) {
                log.w("Call to add participant already in place $principalId")
                throw PincerException("That participant is already in place.")
            }
            log.i("Creating new participant $principalId with roles $roles")
            val participant = Participant(roles = roles)
            if (state.personalSelf()?.name != null) participant.name = state.personalSelf()?.name
            if (state.personalSelf()?.locale != null) participant.locale = state.personalSelf()?.locale!!
            if (state.personalSelf()?.zone != null) participant.zone = state.personalSelf()?.zone!!
            state.pincer()!!.participants[state.remote!!.principalId!!] = participant
            log.i("Reporting local change")
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setRedRules(redRules: String) = wrap("setRedRules", {
        state.assertParticipant()
        if (validateRules(redRules) != null) {
            state.pincerUiSelf()!!.redRules = redRules
            log.w("Ignoring call to setRedRules as is invalid : $redRules")
        }
        else {
            state.pincerSelf()!!.redRules = redRules
            state.pincerUiSelf()!!.redRules = null
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setGreenRules(greenRules: String) = wrap("setGreenRules", {
        state.assertParticipant()
        if (validateRules(greenRules) != null) {
            state.pincerUiSelf()!!.greenRules = greenRules
            log.w("Ignoring call to setGreenRules as is invalid : $greenRules")
        }
        else {
            state.pincerSelf()!!.greenRules = greenRules
            state.pincerUiSelf()!!.greenRules = null
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setStencil(stencil: Boolean) = wrap("setStencil", {
        state.assertParticipant()
        state.pincerSelf()!!.stencil = stencil
        state.remote!!.syncUp(state.transient.pid!!)
    })

    private fun setPincerRules(rules: String) = wrap("setPincerRules", {
        state.assertParticipant()
        if (validateRules(rules) != null) {
            state.pincerUi()!!.rules = rules
            log.w("Ignoring call to setPincerRules as is invalid : $rules")
        }
        else {
            state.pincer()!!.rules = rules
            state.pincerUi()!!.rules = null
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setPincerName(name: String) = wrap("setPincerName", {
        state.assertParticipant()
        if (validateText(name) != null) {
            state.pincerUi()!!.name = name
            log.w("Ignoring call to setPincerName as invalid : ${validateText(name)}")
        }
        else {
            state.pincerUi()!!.name = null
            state.pincer()!!.name = name
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setPincerDescription(description: String) = wrap("setPincerDescription", {
        state.assertParticipant()
        if (validateText(description) != null) {
            state.pincerUi()!!.description = description
            log.w("Ignoring call to setPincerDescription as invalid : ${validateText(description)}")
        }
        else {
            state.pincerUi()!!.description = null
            state.pincer()!!.description = description
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setUseOpenRole(useOpenRole: Boolean) = wrap("setUseOpenRole", {
        state.pincerUi()!!.openRole = null
        state.pincer()!!.openRole = if (useOpenRole) "attendee" else null
        state.remote!!.syncUp(state.transient.pid!!)
    })

    private fun setOpenRole(openRole: String) = wrap("setOpenRole", {
        val openRoleNullable: String? = if (openRole == "") null else openRole
        if (validateRole(openRoleNullable) != null) {
            state.pincerUi()!!.openRole = openRoleNullable
            log.w("Ignoring call to setOpenRole as invalid : ${validateRoles(openRoleNullable)}")
        }
        else {
            state.pincerUi()!!.openRole = null
            state.pincer()!!.openRole = if (openRole == "") null else openRole
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun makeTemplate() = wrap("makeTemplate", {
        state.pincer()!!.pincerType = PincerType.TEMPLATE
        state.pincer()!!.forkRole = "attendee"
        state.remote!!.syncUp(state.transient.pid!!)
    })

    private fun setNewQuestionCode(code: String) = wrap("setNewQuestionCode", {
        state.transient.newQuestion.code = code
    })

    private fun setNewQuestionFormat(format: String) = wrap("setNewQuestionFormat", {
        state.transient.newQuestion.format = format
    })

    private fun setNewQuestionWording(wording: String) = wrap("setNewQuestionWording", {
        state.transient.newQuestion.wording = wording
    })

    private fun setNewQuestionHelpText(helpText: String) = wrap("setNewQuestionHelpText", {
        state.transient.newQuestion.helpText = helpText
    })

    private fun setNewQuestionOptions(options: String) = wrap("setNewQuestionOptions", {
        state.transient.newQuestion.options = options.trim()
    })

    private fun setNewQuestionRegex(regex: String) = wrap("setNewQuestionRegex", {
        state.transient.newQuestion.regex = regex
    })

    private fun setNewQuestionRoles(roles: String) = wrap("setNewQuestionRoles", {
        state.transient.newQuestion.roles = roles
    })

    private fun addQuestion() = wrap("addQuestion", {
        state.assertPincerIsReady()
        // For validation up to this point, null values in required fields mean nothing entereded there yet.
        // But from here, we need to trigger validation problems for them.
        // This technique means we don't complain about empty boxes too soon in the UI.
        if (state.transient.newQuestion.code == null) state.transient.newQuestion.code = ""
        if (state.transient.newQuestion.wording == null) state.transient.newQuestion.wording = ""
        if (state.transient.newQuestion.format == QuestionFormat.SELECT.toString() && state.transient.newQuestion.options == null) state.transient.newQuestion.options = ""
        if (state.transient.newQuestion.format == QuestionFormat.REGEX.toString() && state.transient.newQuestion.regex == null) state.transient.newQuestion.regex = ""
        val question = state.transient.newQuestion.asQuestion()
        val problems = validateQuestion(question).problems
        if (!problems.isEmpty()) {
            log.w("Ignoring call to add question as something is invalid...")
            for (problem in problems) log.w(problem)
        }
        else {
            val questionId = newQuestionId(state.pincer()!!)
            state.pincer()!!.questions[questionId] = question
            state.remote!!.syncUp(state.transient.pid!!)
            state.pincerUi()!!.questions["NEW"] = QuestionDraft(format = "SIMPLE")
        }
    })

    private fun getUiQuestion(questionId: String): QuestionDraft {
        state.assertPincerIsReady()
        if (state.pincerUi()!!.questions.get(questionId) == null) {
            state.pincerUi()!!.questions.set(questionId, QuestionDraft())
        }
        return state.pincerUi()!!.questions.get(questionId)!!
    }

    private fun setQuestionCode(questionId: String, code: String) = wrap("setQuestionCode", {
        state.assertParticipant()
        val uiQuestion = getUiQuestion(questionId)
        if (validateQuestionCode(code) != null) {
            uiQuestion.code = code
            log.w("Ignoring call to setQuestionCode as invalid : $code")
        }
        else {
            uiQuestion.code = null
            state.pincer()!!.questions[questionId]!!.code = code
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setQuestionWording(questionId: String, wording: String) = wrap("setQuestionWording", {
        state.assertParticipant()
        val uiQuestion = getUiQuestion(questionId)
        if (validateText(wording) != null) {
            uiQuestion.wording = wording
            log.w("Ignoring call to setQuestionWording as invalid : $wording")
        }
        else {
            uiQuestion.wording = null
            state.pincer()!!.questions[questionId]!!.wording = wording
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setQuestionFormat(questionId: String, format: String) = wrap("setQuestionFormat", {
        state.assertParticipant()
        state.pincer()!!.questions[questionId]!!.format = format
        state.remote!!.syncUp(state.transient.pid!!)
    })

    private fun setQuestionHelpText(questionId: String, helpText: String) = wrap("setQuestionHelpText", {
        state.assertParticipant()
        val uiQuestion = getUiQuestion(questionId)
        if (validateText(helpText) != null) {
            uiQuestion.helpText = helpText
            log.w("Ignoring call to setQuestionHelpText as invalid : $helpText")
        }
        else {
            uiQuestion.helpText = null
            state.pincer()!!.questions[questionId]!!.helpText = helpText
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setQuestionOptions(questionId: String, options: String) = wrap("setQuestionOptions", {
        state.assertParticipant()
        val uiQuestion = getUiQuestion(questionId)
        if (validateOptions(options) != null) {
            uiQuestion.options = options
            log.w("Ignoring call to setQuestionOptions as invalid : $options")
        }
        else {
            uiQuestion.options = null
            state.pincer()!!.questions[questionId]!!.options = options
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setQuestionRegex(questionId: String, regex: String) = wrap("setQuestionRegex", {
        state.assertParticipant()
        val uiQuestion = getUiQuestion(questionId)
        if (validateRoles(regex) != null) {
            uiQuestion.regex = regex
            log.w("Ignoring call to setQuestionRegex as invalid : $regex")
        }
        else {
            uiQuestion.regex = null
            state.pincer()!!.questions[questionId]!!.regex = regex
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setQuestionRoles(questionId: String, roles: String) = wrap("setQuestionRoles", {
        state.assertParticipant()
        val uiQuestion = getUiQuestion(questionId)
        if (validateRoles(roles) != null) {
            uiQuestion.roles = roles
            log.w("Ignoring call to setQuestionRoles as invalid : $roles")
        }
        else {
            uiQuestion.roles = null
            state.pincer()!!.questions[questionId]!!.roles = roles
            state.remote!!.syncUp(state.transient.pid!!)
        }
    })

    private fun setAnswer(questionId: String, wording: String) = wrap("setAnswer", {
        state.assertParticipant()
        upsertAnswer(pincer = state.pincer()!!, questionIdOrCode = questionId, participant = state.pincerSelf()!!, wording = wording)
        state.remote!!.syncUp(state.transient.pid!!)
    })

    private fun principalTriggerCode(claimedCredential: String) = wrap("principalTriggerCode", {
        if (state.personalPincer()?.name != null) throw Exception("Claiming a second credential?")
        state.transient.claimedCredential = claimedCredential
        if (validateCredential(claimedCredential) != null) {
            log.i("No action on principalTriggerCode as invalid : ${claimedCredential}")
        }
        else {
            log.i("Claimed credential ${claimedCredential}")
            try {
                state.remote!!.principalTriggerCode(claimedCredential)
                state.transient.expectingCode = true
            }
            catch (lockedPrincipal: LockedPrincipal) {
                log.w("Caught locked signal when claiming credential ${lockedPrincipal.message}")
                state.transient.lockedPrincipalUntil = lockedPrincipal.message.toLong()
            }
        }
    })

    private fun principalConfirmCode(credentialCode: String) = wrap("principalConfirmCode", {
        state.transient.credentialCode = credentialCode
        if (validateCode(state.transient.credentialCode) != null) {
            log.i("No action on credentialCode as code is invalid : ${state.transient.credentialCode}")
        }
        else {
            log.i("Access code ${state.transient.claimedCredential} / ${state.transient.credentialCode}")
            try {
                val pi = state.remote!!.principalConfirmCode(state.transient.claimedCredential!!, state.transient.credentialCode!!, locale = state.stored.fallbackLocale, zone = state.stored.fallbackZone)
                log.i("Successfully logged in principalId: $pi")
                state.transient.credentialCodeMayBeWrong = false
                state.transient.claimedCredential = null
            }
            catch (lockedPrincipal: LockedPrincipal) {
                log.w("Locked prinicipal while claiming code, ${lockedPrincipal.message}")
                state.transient.lockedPrincipalUntil = lockedPrincipal.message.toLong()
                state.transient.credentialCodeMayBeWrong = true
            }
            state.transient.expectingCode = false
            state.transient.credentialCode = null
        }
    })

    private fun logoutThisDevice() = wrap("logoutThisDevice", { logoutInner(false) })

    private fun logoutAllDevices() =  wrap("logoutAllDevices", { logoutInner(true) })

    private suspend fun logoutInner(allDevices: Boolean) {
        log.i("Logout, allDevices:$allDevices")
        state.remote!!.principalLogout(allDevices)
        log.i("Logout signal sent to server")
        state.resetTransientState()
        state.resetRemoteState(coroutineScope)
    }

    @JsName("logJwt")
    fun logJwt() = state.remote?.logJwt()

    @JsName("logStoredState")
    fun logStoredState() = state.stored.logAll()

    @JsName("logPincer")
    fun logPincer() = log.i("Pincer: " + state.pincer()?.json())

    @JsName("logPersonalPincer")
    fun logPersonalPincer() = log.i("PersonalPincer: " + state.personalPincer()?.json())

}
